import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import {
  DndContext,
  PointerSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  closestCenter,
} from '@dnd-kit/core';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { Lock, LockOpen, Trophy, Warning, ArrowUp, ArrowDown, Equals, X } from '@phosphor-icons/react';

/* ------------------------------------------------------------------ */
/* Visual-only player card (no drag wiring). Used as the rendered      */
/* output of both the draggable PlayerCard and (until we removed it)   */
/* the DragOverlay.                                                    */
/* ------------------------------------------------------------------ */
const PlayerCardVisual = React.forwardRef(({ player, state = 'idle', disabled = false, style, teamLetter, teamColor, ...rest }, ref) => {
  const lvl = player.user_level == null ? '?' : Number(player.user_level).toFixed(2);
  const cls = state === 'dragging'
    ? 'border-purple-500 bg-purple-50 shadow-xl cursor-grabbing ring-2 ring-purple-300 z-50'
    : disabled
      ? 'border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed'
      : 'border-slate-200 bg-white hover:border-purple-300 hover:bg-purple-50/40 cursor-grab active:cursor-grabbing';
  return (
    <div
      ref={ref}
      style={style}
      data-testid={`lineup-player-${player.user_id}`}
      className={`select-none touch-none px-2.5 py-1.5 rounded-lg border text-xs font-medium flex items-center justify-between gap-2 transition-colors ${cls}`}
      {...rest}
    >
      <span className="truncate flex-1 flex items-center gap-1">
        {teamLetter && (
          <span
            className={`shrink-0 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-extrabold uppercase tracking-wide ${teamColor || 'bg-pink-100 text-pink-700'}`}
            title={`Signup-Team ${teamLetter}`}
            data-testid={`lineup-team-badge-${player.user_id}`}
          >
            🤝 {teamLetter}
          </span>
        )}
        <span className="truncate">{player.user_name}</span>
        {player.user_is_member && <span className="ml-1 text-sky-500">✓</span>}
      </span>
      <span className="shrink-0 text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
        L{lvl}
      </span>
    </div>
  );
});
PlayerCardVisual.displayName = 'PlayerCardVisual';

/* ------------------------------------------------------------------ */
/* Draggable player card                                              */
/* ------------------------------------------------------------------ */
/* Why we apply useDraggable.transform directly (no <DragOverlay>):
   the Shadcn Dialog this board lives in centers itself with
   `transform: translate(-50%, -50%)`. <DragOverlay> renders via a
   React portal at <body>, so its coordinate frame is the viewport
   while the source card's frame is the translated dialog. The two
   frames don't match → the floating pill drifts.  Applying the
   useDraggable transform to the source card itself keeps everything
   in the same (translated) frame, so the pill follows the cursor
   1:1 at any zoom level and on touch devices.                       */
const PlayerCard = ({ player, disabled, dropId, teamLetter, teamColor }) => {
  const { attributes, listeners, setNodeRef: setDragRef, transform, isDragging } = useDraggable({
    id: player.user_id, disabled, data: { player },
  });
  // Also make each seated player a drop target so admins can drop another
  // player ON them to SWAP positions (both keep their courts if same court;
  // different-court swap moves each to the other's spot).
  const { isOver, setNodeRef: setDropRef } = useDroppable({
    id: dropId || `swap-${player.user_id}`, disabled, data: { swapWith: player.user_id },
  });
  const style = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }
    : undefined;
  return (
    <div ref={setDropRef} className={isOver ? 'ring-2 ring-purple-400 rounded-lg' : undefined}>
      <PlayerCardVisual
        ref={setDragRef}
        player={player}
        disabled={disabled}
        state={isDragging ? 'dragging' : 'idle'}
        style={style}
        teamLetter={teamLetter}
        teamColor={teamColor}
        {...listeners}
        {...attributes}
      />
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Empty spot placeholder — also a droppable so admins can target a    */
/* specific position (1..N) on a court, not just "the court somewhere" */
/* ------------------------------------------------------------------ */
const EmptySpot = ({ index, disabled, dropId, isParking = false }) => {
  const { isOver, setNodeRef } = useDroppable({
    id: dropId || `empty-spot-${index}`, disabled,
  });
  return (
    <div
      ref={setNodeRef}
      data-testid={`lineup-empty-spot-${index}`}
      className={`px-2.5 py-1.5 rounded-lg border border-dashed text-[11px] flex items-center justify-center gap-2 transition-all ${
        isOver
          ? 'border-purple-500 bg-purple-100 text-purple-700 ring-2 ring-purple-300'
          : disabled
            ? 'border-slate-200 bg-slate-50/40 text-slate-300'
            : isParking
              ? 'border-amber-300 bg-amber-50/40 text-amber-600'
              : 'border-purple-200 bg-purple-50/30 text-purple-400'
      }`}
    >
      <span className="font-semibold tracking-wide uppercase">{index}</span>
      <span className="opacity-70">·</span>
      <span className="italic">{isParking ? 'Parken' : 'Frei'}</span>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Drop zone (court or waitlist)                                      */
/* ------------------------------------------------------------------ */
const ZONE_STYLES = {
  emerald: { border: 'border-emerald-200', bg: 'bg-emerald-50/30', text: 'text-emerald-800', accent: 'text-emerald-600', bar: 'bg-emerald-400' },
  slate:   { border: 'border-slate-200',   bg: 'bg-slate-50/30',   text: 'text-slate-800',   accent: 'text-slate-600',   bar: 'bg-slate-400' },
  rose:    { border: 'border-rose-200',    bg: 'bg-rose-50/30',    text: 'text-rose-800',    accent: 'text-rose-600',    bar: 'bg-rose-400' },
  amber:   { border: 'border-amber-200',   bg: 'bg-amber-50/30',   text: 'text-amber-800',   accent: 'text-amber-600',   bar: 'bg-amber-400' },
  purple:  { border: 'border-purple-300',  bg: 'bg-purple-50/40',  text: 'text-purple-800',  accent: 'text-purple-600',  bar: 'bg-purple-400' },
};

const Zone = ({ id, label, sublabel, players, capacity, color, children, disabled }) => {
  const { isOver, setNodeRef } = useDroppable({ id, disabled });
  const fill = capacity ? Math.min(100, (players.length / capacity) * 100) : 0;
  const overflow = capacity != null && players.length > capacity;
  const s = ZONE_STYLES[color] || ZONE_STYLES.slate;
  return (
    <div
      ref={setNodeRef}
      data-testid={`lineup-zone-${id}`}
      className={`rounded-xl border-2 transition-all p-3 min-h-[140px] ${
        isOver
          ? 'border-purple-500 bg-purple-50 shadow-md'
          : overflow
            ? 'border-amber-300 bg-amber-50/40'
            : `${s.border} ${s.bg}`
      }`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-baseline gap-1.5">
          <p className={`text-sm font-bold ${s.text} leading-none`}>{label}</p>
          {sublabel && <p className="text-[10px] text-slate-400">{sublabel}</p>}
        </div>
        <span className={`text-[11px] font-semibold ${overflow ? 'text-amber-700' : s.accent}`}>
          {players.length}{capacity != null ? `/${capacity}` : ''}
        </span>
      </div>
      {capacity != null && (
        <div className="h-1 rounded-full bg-slate-100 mb-2 overflow-hidden">
          <div className={`h-full ${overflow ? 'bg-amber-400' : s.bar}`} style={{ width: `${fill}%` }} />
        </div>
      )}
      <div className="space-y-1.5">
        {children}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Main board                                                         */
/* ------------------------------------------------------------------ */
const MixinLineupBoard = ({ event, mode, open, onClose, onUpdated }) => {
  const { API, language } = useAuth();
  const L = (en, deu) => (language === 'de' ? deu : en);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // mode: 'lineup' = pre-start drag/drop, 'results' = post-event drag/drop
  // Local court assignment map: { user_id: courtNumber | 'waitlist' | null }
  const [courtMap, setCourtMap] = useState({});
  const [winners, setWinners] = useState([]);
  // Manual ordering per user — increments on every drop so the most-recently
  // dropped player lands at the end of the destination court's stack.
  // Overrides the default level-sort so admins can freely arrange positions
  // (e.g. put the fresh/tall player as position 1 for tactical reasons).
  const [manualOrder, setManualOrder] = useState({});
  const orderCounterRef = useRef(1);
  // activeId state removed — was only used to feed <DragOverlay>; we now
  // move the source card itself via useDraggable.transform, see PlayerCard.
  const [confirming, setConfirming] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 120, tolerance: 5 } }),
    useSensor(KeyboardSensor),
  );

  const fetchLineup = useCallback(async () => {
    if (!event?.id) return;
    setLoading(true);
    try {
      const { data: d } = await API.get(`/community/events/${event.id}/lineup`);
      setData(d);
      setWinners((d.confirmed || []).filter(p => p.is_winner).map(p => p.user_id));
      // Seed local courtMap
      const map = {};
      if (mode === 'results') {
        // In results mode, ALL confirmed players start in the "pool" (waitlist
        // zone) UNLESS a final_court was already recorded. This forces the
        // admin to drag each player onto the court where they finished,
        // instead of confusingly pre-seeding them with start_court positions.
        (d.confirmed || []).forEach(p => {
          map[p.user_id] = p.final_court != null ? p.final_court : 'waitlist';
        });
      } else {
        (d.confirmed || []).forEach(p => {
          map[p.user_id] = p.start_court || null;
        });
      }
      (d.waitlist || []).forEach(p => { map[p.user_id] = 'waitlist'; });
      setCourtMap(map);
      // Seed manualOrder from any persisted `court_position` so the admin's
      // saved arrangement survives reload / confirmation. Position becomes
      // the sort key directly (lower = earlier). Also wipe manual positions
      // so the level-sort is truly active again.
      const seededOrder = {};
      let maxSeen = 0;
      (d.confirmed || []).forEach(p => {
        const pos = p.court_position;
        if (typeof pos === 'number' && pos > 0) {
          seededOrder[p.user_id] = pos;
          if (pos > maxSeen) maxSeen = pos;
        }
      });
      setManualOrder(seededOrder);
      orderCounterRef.current = maxSeen + 1;
    } catch (e) {
      toast.error(L('Could not load lineup', 'Aufstellung konnte nicht geladen werden'));
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event?.id, mode]);

  useEffect(() => { if (open) fetchLineup(); }, [open, fetchLineup]);

  const allPlayers = useMemo(() => {
    if (!data) return [];
    return [...(data.confirmed || []), ...(data.waitlist || [])];
  }, [data]);

  const playerById = useMemo(() => {
    const m = {};
    allPlayers.forEach(p => { m[p.user_id] = p; });
    return m;
  }, [allPlayers]);

  const numCourts = data?.num_courts || event?.num_courts || 8;
  const perCourt = data?.players_per_court || event?.players_per_court || 4;

  const playersOnCourt = useCallback((courtNum) => {
    return allPlayers
      .filter(p => courtMap[p.user_id] === courtNum)
      .sort((a, b) => {
        // Manual positions win — a lower counter means "further up the
        // court's stack". Once materialized on first drop, this replaces
        // the level-sort so admin retains full control. Players still
        // sorted by level only until admin touches the court.
        const oa = manualOrder[a.user_id];
        const ob = manualOrder[b.user_id];
        if (oa != null && ob != null) return oa - ob;
        if (oa != null) return -1;  // manually placed FIRST
        if (ob != null) return 1;
        const la = a.user_level == null ? 99 : Number(a.user_level);
        const lb = b.user_level == null ? 99 : Number(b.user_level);
        return la - lb;
      });
  }, [allPlayers, courtMap, manualOrder]);

  // Team-letter mapping for signup-pair badges. Any two players with the
  // same `signup_group_id` share a letter (A/B/C…) and a distinct color so
  // admin sees which pairs must stay together. Solo signups get no badge.
  const teamPalette = useMemo(() => [
    'bg-pink-100 text-pink-700',
    'bg-indigo-100 text-indigo-700',
    'bg-orange-100 text-orange-700',
    'bg-teal-100 text-teal-700',
    'bg-fuchsia-100 text-fuchsia-700',
    'bg-lime-100 text-lime-700',
    'bg-cyan-100 text-cyan-700',
    'bg-rose-100 text-rose-700',
  ], []);
  const teamInfoByUser = useMemo(() => {
    const groupsSeen = new Map(); // gid -> letter idx
    let nextIdx = 0;
    const out = {};
    // Deterministic order — sort by signup time so pair labels are stable
    // between renders.
    const sorted = [...(allPlayers || [])].sort((a, b) =>
      String(a.signed_up_at || '').localeCompare(String(b.signed_up_at || ''))
    );
    for (const p of sorted) {
      const gid = p.signup_group_id;
      if (!gid) continue;
      if (!groupsSeen.has(gid)) {
        groupsSeen.set(gid, nextIdx);
        nextIdx += 1;
      }
      const idx = groupsSeen.get(gid);
      const letter = String.fromCharCode(65 + (idx % 26));
      out[p.user_id] = { letter, color: teamPalette[idx % teamPalette.length] };
    }
    return out;
  }, [allPlayers, teamPalette]);

  const playersOnWaitlist = useMemo(() => {
    return allPlayers.filter(p => courtMap[p.user_id] === 'waitlist' || courtMap[p.user_id] == null);
  }, [allPlayers, courtMap]);

  // Lineup is invalid only when over total capacity. Lone-1 short courts
  // are OK — that just signals "3 more players needed on the last court".
  // Final level balancing happens at T-2h via the auto-finalize background task.
  const confirmedCount = useMemo(() => {
    if (!data) return 0;
    return (data.confirmed || []).length;
  }, [data]);
  const capacity = (perCourt || 4) * (numCourts || 8);
  const overCapacity = Math.max(0, confirmedCount - capacity);
  const isInvalid = overCapacity > 0;
  const trimTarget = capacity;

  const [trimming, setTrimming] = useState(false);
  const handleAutoTrim = async () => {
    if (!event?.id || trimming) return;
    setTrimming(true);
    try {
      const { data: res } = await API.post(`/community/events/${event.id}/lineup/trim-to-multiple`);
      toast.success(L(
        `${res.trimmed} player(s) moved to waitlist — they have been notified.`,
        `${res.trimmed} Spieler auf Warteliste verschoben — Benachrichtigung gesendet.`,
      ));
      await fetchLineup();
    } catch (e) {
      toast.error(e.response?.data?.detail || L('Could not trim', 'Auto-Trim fehlgeschlagen'));
    } finally {
      setTrimming(false);
    }
  };


  // Helper (legacy): assign every player on `courtNum` an explicit
  // manualOrder based on their current visual position. Kept for the
  // legacy zone-background drop case below where a target position isn't
  // known and we just append the dropped player to the court's stack.
  const materializeCourt = useCallback((courtNum, currentOrder) => {
    const out = { ...currentOrder };
    const list = allPlayers
      .filter(p => courtMap[p.user_id] === courtNum)
      .sort((a, b) => {
        const oa = out[a.user_id];
        const ob = out[b.user_id];
        if (oa != null && ob != null) return oa - ob;
        if (oa != null) return -1;
        if (ob != null) return 1;
        const la = a.user_level == null ? 99 : Number(a.user_level);
        const lb = b.user_level == null ? 99 : Number(b.user_level);
        return la - lb;
      });
    list.forEach(p => {
      if (out[p.user_id] == null) out[p.user_id] = orderCounterRef.current++;
    });
    return out;
  }, [allPlayers, courtMap]);

  // Rebuild manualOrder for a whole court, inserting `playerId` at exact
  // `targetPos` (1-based). This is the correct primitive for "drop into
  // position 3": every seated player on the target court is re-assigned a
  // fresh contiguous order value so the sort matches the visual order 1:1.
  // Without this, dropping into position 3 just bumped the player to the
  // front of the level-sorted list, which felt broken to admins.
  const reorderCourtWithInsertion = useCallback((playerId, targetCourt, targetPos, currentOrder) => {
    const existingIds = allPlayers
      .filter(p => courtMap[p.user_id] === targetCourt && p.user_id !== playerId)
      .sort((a, b) => {
        const oa = currentOrder[a.user_id];
        const ob = currentOrder[b.user_id];
        if (oa != null && ob != null) return oa - ob;
        if (oa != null) return -1;
        if (ob != null) return 1;
        const la = a.user_level == null ? 99 : Number(a.user_level);
        const lb = b.user_level == null ? 99 : Number(b.user_level);
        return la - lb;
      })
      .map(p => p.user_id);
    const insertIdx = Math.min(Math.max(0, targetPos - 1), existingIds.length);
    const newList = [...existingIds.slice(0, insertIdx), playerId, ...existingIds.slice(insertIdx)];
    const base = orderCounterRef.current;
    const next = { ...currentOrder };
    newList.forEach((uid, i) => { next[uid] = base + i; });
    orderCounterRef.current = base + newList.length + 10;  // gap to avoid collisions
    return next;
  }, [allPlayers, courtMap]);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over) return;
    const playerId = active.id;
    const dest = String(over.id);

    // --- Case 1: SWAP — dropped directly on a seated player card ---
    // Drop ID: swap-court-{N}-pos-{P}-user-{USERID}
    if (dest.startsWith('swap-court-')) {
      const m = dest.match(/^swap-court-(\d+)-pos-(\d+)-user-(.+)$/);
      if (!m) return;
      const targetCourt = parseInt(m[1], 10);
      const targetPos = parseInt(m[2], 10);
      const targetUserId = m[3];
      if (targetUserId === playerId) return;
      const draggedCourt = courtMap[playerId];
      // Insert dragged player at target's exact position, kicking the
      // target one slot down. If same court, this feels like a re-order;
      // if different court, the target keeps its old court but the
      // dragged one takes the exact position they were on.
      setManualOrder(o => {
        // First insert dragged at targetPos on targetCourt
        let next = reorderCourtWithInsertion(playerId, targetCourt, targetPos, o);
        // If the dragged came from another court, that court also needs
        // to be re-materialized so its positions stay contiguous.
        if (draggedCourt !== targetCourt && typeof draggedCourt === 'number') {
          const remaining = allPlayers
            .filter(p => courtMap[p.user_id] === draggedCourt && p.user_id !== playerId)
            .sort((a, b) => (next[a.user_id] ?? 99) - (next[b.user_id] ?? 99))
            .map(p => p.user_id);
          const base = orderCounterRef.current;
          remaining.forEach((uid, i) => { next[uid] = base + i; });
          orderCounterRef.current = base + remaining.length + 10;
        }
        return next;
      });
      setCourtMap(mm => ({ ...mm, [playerId]: targetCourt }));
      return;
    }

    // --- Case 2: Empty position slot on a specific court ---
    // Drop ID: empty-court-{N}-pos-{P}
    if (dest.startsWith('empty-court-')) {
      const m = dest.match(/^empty-court-(\d+)-pos-(\d+)$/);
      if (!m) return;
      const targetCourt = parseInt(m[1], 10);
      const targetPos = parseInt(m[2], 10);
      setManualOrder(o => reorderCourtWithInsertion(playerId, targetCourt, targetPos, o));
      setCourtMap(mm => ({ ...mm, [playerId]: targetCourt }));
      return;
    }

    // --- Case 3: Legacy generic zone drop (court background / waitlist) ---
    let newDest;
    if (dest === 'waitlist') newDest = 'waitlist';
    else if (dest.startsWith('court-')) newDest = parseInt(dest.replace('court-', ''));
    else return;

    // Note: courts can temporarily go over `perCourt` (e.g. 5/4 in amber)
    // so admins can drop a player in first and *then* move someone else
    // out — supporting the "swap" workflow. For a specific position,
    // drop directly onto a player card (swap) or empty slot instead.
    setCourtMap(mm => ({ ...mm, [playerId]: newDest }));
    setManualOrder(o => ({ ...o, [playerId]: orderCounterRef.current++ }));
  };

  const isLocked = mode === 'lineup' && data?.lineup_locked;
  const finalized = data?.results_finalized;

  /* ------------------------------- Save / Confirm -------------------------------- */
  // Compute court_position (1-based) for every seated player based on
  // their current visual order. Sent to the backend so the admin's manual
  // arrangement survives save / confirmation / reload.
  const computePositions = useCallback(() => {
    const positions = {};
    for (let c = 1; c <= (data?.num_courts || numCourts || 8); c++) {
      const players = playersOnCourt(c);
      players.forEach((p, idx) => { positions[p.user_id] = idx + 1; });
    }
    return positions;
  }, [data?.num_courts, numCourts, playersOnCourt]);

  const handleSave = async () => {
    if (!event?.id) return;
    setSaving(true);
    try {
      const positions = computePositions();
      const assignments = allPlayers.map(p => {
        const v = courtMap[p.user_id];
        if (mode === 'lineup') {
          return {
            user_id: p.user_id,
            court: typeof v === 'number' ? v : null,
            court_position: positions[p.user_id] || null,
          };
        }
        return { user_id: p.user_id, final_court: typeof v === 'number' ? v : null };
      });
      const url = mode === 'lineup'
        ? `/community/events/${event.id}/lineup`
        : `/community/events/${event.id}/results`;
      await API.put(url, mode === 'lineup' ? { assignments } : { assignments, winner_user_ids: winners });
      toast.success(L('Saved', 'Gespeichert'));
      onUpdated?.();
      fetchLineup();
    } catch (e) {
      toast.error(e.response?.data?.detail || L('Save failed', 'Speichern fehlgeschlagen'));
    } finally {
      setSaving(false);
    }
  };

  const handleConfirm = async () => {
    if (!event?.id) return;
    setConfirming(true);
    try {
      // Save first, then confirm/finalize
      const positions = computePositions();
      const assignments = allPlayers.map(p => {
        const v = courtMap[p.user_id];
        if (mode === 'lineup') {
          return {
            user_id: p.user_id,
            court: typeof v === 'number' ? v : null,
            court_position: positions[p.user_id] || null,
          };
        }
        return { user_id: p.user_id, final_court: typeof v === 'number' ? v : null };
      });
      if (mode === 'lineup') {
        await API.put(`/community/events/${event.id}/lineup`, { assignments });
        await API.post(`/community/events/${event.id}/lineup/confirm`);
        toast.success(L('Lineup confirmed — players notified', 'Aufstellung bestätigt — Spieler benachrichtigt'));
      } else {
        await API.put(`/community/events/${event.id}/results`, { assignments, winner_user_ids: winners });
        const { data: res } = await API.post(`/community/events/${event.id}/results/finalize`);
        const s = res.summary || {};
        toast.success(L(
          `Levels updated: ${s.total_updated} players (↑${s.improved} · ↓${s.worse} · =${s.unchanged})`,
          `Levels aktualisiert: ${s.total_updated} Spieler (↑${s.improved} · ↓${s.worse} · =${s.unchanged})`,
        ));
      }
      onUpdated?.();
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || L('Confirm failed', 'Bestätigung fehlgeschlagen'));
    } finally {
      setConfirming(false);
    }
  };

  const handleUnlock = async () => {
    try {
      await API.post(`/community/events/${event.id}/lineup/unlock`);
      toast.success(L('Lineup unlocked', 'Aufstellung freigegeben'));
      fetchLineup();
      onUpdated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || L('Unlock failed', 'Freigabe fehlgeschlagen'));
    }
  };

  const handleResetToDefault = () => {
    if (!data) return;
    // Re-derive default ordering: sort confirmed by level, chunk by perCourt
    const confirmed = (data.confirmed || []).slice().sort((a, b) => {
      const la = a.user_level == null ? 99 : Number(a.user_level);
      const lb = b.user_level == null ? 99 : Number(b.user_level);
      return la - lb;
    });
    const map = {};
    confirmed.forEach((p, idx) => {
      let c = Math.floor(idx / Math.max(1, perCourt)) + 1;
      if (c > numCourts) c = numCourts;
      map[p.user_id] = c;
    });
    (data.waitlist || []).forEach(p => { map[p.user_id] = 'waitlist'; });
    setCourtMap(map);
    // Also wipe manual positions so the level-sort is truly active again.
    setManualOrder({});
    orderCounterRef.current = 1;
  };

  /* ------------------------------- Level preview (results mode) -------------------------------- */
  // Must mirror the backend formula in /app/backend/routes/community.py:
  //   LEVEL_PER_COURT_DELTA = 0.05, LEVEL_MAX_DELTA = 0.15, 2-decimal round.
  // Prior values (0.10 / 0.30 and 1-decimal rounding) made the preview
  // disagree with what was actually written to the user's player_level on
  // finalize and swallowed the ±0.05 single-court nudges in the display.
  const levelChanges = useMemo(() => {
    if (mode !== 'results' || !data) return [];
    const round2 = (x) => Math.round(x * 100) / 100;
    return (data.confirmed || []).map(p => {
      const startCourt = p.start_court;
      const finalCourt = courtMap[p.user_id];
      if (!startCourt || typeof finalCourt !== 'number' || p.user_level == null) {
        return { ...p, change: 0, before: p.user_level, after: p.user_level, missing: true };
      }
      const raw = (finalCourt - startCourt) * 0.05;
      const capped = Math.max(-0.15, Math.min(0.15, raw)) - (winners.includes(p.user_id) ? 0.05 : 0);
      const before = Number(p.user_level);
      const after = round2(Math.max(1.0, Math.min(7.9, before + capped)));
      return { ...p, change: round2(after - before), before: round2(before), after, missing: false };
    });
  }, [mode, data, courtMap, winners]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-6xl max-h-[92vh] overflow-y-auto" data-testid="lineup-board-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold flex items-center gap-2">
            {mode === 'lineup' ? (
              <>{isLocked ? <Lock size={18} weight="fill" className="text-emerald-600" /> : <LockOpen size={18} className="text-purple-500" />}
              {L('Lineup — drag & drop courts', 'Aufstellung — Courts per Drag & Drop')}</>
            ) : (
              <><Trophy size={18} weight="fill" className="text-amber-500" />
              {L('Final results — drag players to their final court', 'Ergebnisse — Spieler auf den Endcourt ziehen')}</>
            )}
          </DialogTitle>
          <DialogDescription className="text-slate-500">
            {event?.title} · {event?.date} · {event?.start_time}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <p className="py-12 text-center text-slate-400">{L('Loading…', 'Lade…')}</p>
        ) : (
          <>
            {/* Status banner */}
            {mode === 'lineup' && isLocked && (
              <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 mb-3" data-testid="lineup-locked-banner">
                <div className="flex items-center gap-2 text-sm text-emerald-800">
                  <Lock size={14} weight="fill" />
                  {L('Lineup confirmed — players have been notified.', 'Aufstellung bestätigt — Spieler wurden benachrichtigt.')}
                </div>
                <Button variant="outline" size="sm" onClick={handleUnlock} className="text-emerald-700 border-emerald-300 hover:bg-emerald-100" data-testid="lineup-unlock-btn">
                  {L('Unlock', 'Freigeben')}
                </Button>
              </div>
            )}
            {mode === 'results' && finalized && (
              <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3 text-sm text-amber-800">
                <Trophy size={14} weight="fill" />
                {L('Results finalized — levels already applied.', 'Ergebnisse abgeschlossen — Levels wurden angepasst.')}
              </div>
            )}
            {mode === 'results' && !data?.lineup_locked && (
              <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3 text-sm text-amber-800">
                <Warning size={14} weight="fill" />
                {L('Confirm the starting lineup first before entering results.', 'Bitte zuerst die Start-Aufstellung bestätigen.')}
              </div>
            )}

            {mode === 'lineup' && !isLocked && (
              <div className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 mb-3" data-testid="lineup-howto">
                {L(
                  'Drag any player from one court to another, or to/from the Waitlist on the right. Each court has 4 fixed spots — empty spots stay open until filled. To swap two players, drop the new one in first (court goes 5/4 in amber), then move the old one out.',
                  'Spieler per Drag & Drop von Court zu Court oder zur Warteliste rechts verschieben. Jeder Court hat 4 feste Plätze — leere Plätze bleiben offen, bis sie gefüllt sind. Zum Tauschen einfach den neuen Spieler einfügen (Court zeigt dann 5/4 in Amber) und danach den alten Spieler verschieben.',
                )}
              </div>
            )}

            {/* Invalid-lineup banner: only when over capacity */}
            {mode === 'lineup' && !isLocked && isInvalid && (
              <div className="bg-amber-50 border border-amber-300 rounded-lg px-4 py-3 mb-3 flex items-start gap-3" data-testid="lineup-multiple-warning">
                <Warning size={18} weight="fill" className="text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1 text-sm">
                  <p className="font-bold text-amber-800">
                    {L(
                      `${confirmedCount} confirmed players exceeds capacity (${capacity}). Move ${overCapacity} to waitlist.`,
                      `${confirmedCount} bestätigte Spieler überschreiten die Kapazität (${capacity}). ${overCapacity} auf Warteliste verschieben.`,
                    )}
                  </p>
                  <p className="text-xs text-amber-700 mt-0.5">
                    {L(
                      `Auto-trim moves the most-recently-joined ${overCapacity} player(s) to the waitlist. Affected players are notified automatically.`,
                      `Auto-Trim verschiebt die zuletzt beigetretenen ${overCapacity} Spieler auf die Warteliste. Betroffene Spieler werden automatisch benachrichtigt.`,
                    )}
                  </p>
                  <button
                    type="button"
                    onClick={handleAutoTrim}
                    disabled={trimming}
                    data-testid="lineup-auto-trim"
                    className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold disabled:opacity-60"
                  >
                    {trimming ? '…' : L(
                      `Auto-trim to ${trimTarget}`,
                      `Auto-Trim auf ${trimTarget}`,
                    )}
                  </button>
                </div>
              </div>
            )}

            {mode === 'results' && !finalized && (
              <div className="text-[11px] text-purple-700 bg-purple-50 border border-purple-200 rounded-lg px-3 py-2 mb-3" data-testid="results-howto">
                {L(
                  'All players start in the pool on the right. Drag each player onto the court where they finished the tournament. Every court advanced = +0.05 level (capped at ±0.15 per session). Tournament winner: +0.05 extra.',
                  'Alle Spieler starten rechts im Pool. Ziehe jeden Spieler auf den Court, auf dem er/sie das Turnier beendet hat. Jeder Court-Aufstieg = +0.05 Level (max. ±0.15 pro Session). Turniersieger: +0.05 extra.',
                )}
              </div>
            )}

            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-4">
                {/* Courts grid */}
                <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
                  {Array.from({ length: numCourts }).map((_, i) => {
                    const courtNum = i + 1;
                    const players = playersOnCourt(courtNum);
                    return (
                      <Zone
                        key={courtNum}
                        id={`court-${courtNum}`}
                        label={`Court ${courtNum}`}
                        sublabel={courtNum === 1 ? L('top', 'oben') : (courtNum === numCourts ? L('bottom', 'unten') : null)}
                        players={players}
                        capacity={perCourt}
                        color={courtNum === 1 ? 'emerald' : (courtNum === numCourts ? 'rose' : 'slate')}
                        disabled={isLocked || finalized}
                      >
                        {/* Render players first, then at least 1 empty
                            "parking" slot (labelled + or a position number)
                            even if the court is at/over capacity — so
                            admins can freely drop a 5th, 6th player to
                            temporarily park them while moving people
                            around. Total dashed slots = at least 1, and
                            at least (perCourt - players.length) when the
                            court is not yet full. */}
                        {players.map((p, idx) => {
                          const team = teamInfoByUser[p.user_id];
                          return (
                            <PlayerCard
                              key={p.user_id}
                              player={p}
                              disabled={isLocked || finalized}
                              dropId={`swap-court-${courtNum}-pos-${idx + 1}-user-${p.user_id}`}
                              teamLetter={team?.letter}
                              teamColor={team?.color}
                            />
                          );
                        })}
                        {Array.from({ length: Math.max(1, perCourt - players.length) }).map((_, idx) => {
                          const posNum = players.length + idx + 1;
                          const isParking = posNum > perCourt;
                          return (
                            <EmptySpot
                              key={`empty-${courtNum}-${idx}`}
                              index={posNum}
                              disabled={isLocked || finalized}
                              dropId={`empty-court-${courtNum}-pos-${posNum}`}
                              isParking={isParking}
                            />
                          );
                        })}
                      </Zone>
                    );
                  })}
                </div>

                {/* Sidebar: waitlist + (results-mode) preview */}
                <div className="space-y-3">
                  <Zone
                    id="waitlist"
                    label={mode === 'results' ? L('Player pool', 'Spieler-Pool') : L('Waitlist / Bench', 'Warteliste / Bank')}
                    players={playersOnWaitlist}
                    capacity={null}
                    color={mode === 'results' ? 'purple' : 'amber'}
                    disabled={isLocked || finalized}
                  >
                    {playersOnWaitlist.map(p => {
                      const team = teamInfoByUser[p.user_id];
                      return (
                        <PlayerCard
                          key={p.user_id}
                          player={p}
                          disabled={isLocked || finalized}
                          teamLetter={team?.letter}
                          teamColor={team?.color}
                        />
                      );
                    })}
                    {playersOnWaitlist.length === 0 && (
                      <p className="text-[11px] text-slate-400 italic">
                        {mode === 'results'
                          ? L('All players placed ✓', 'Alle Spieler platziert ✓')
                          : L('No bench players', 'Keine Bank-Spieler')}
                      </p>
                    )}
                  </Zone>

                  {mode === 'results' && (
                    <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-3" data-testid="winner-panel">
                      <p className="text-xs font-bold uppercase tracking-wider text-amber-700 mb-1 flex items-center gap-1">
                        <Trophy size={12} weight="fill" /> {L('Tournament winner', 'Turniersieger')}
                      </p>
                      <p className="text-[10px] text-amber-700 mb-2">{L('Tap winner(s) — +0.05 extra level each.', 'Sieger antippen — je +0.05 Level extra.')}</p>
                      <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto">
                        {[...(data?.confirmed || [])]
                          .sort((a, b) => (courtMap[a.user_id] === 1 ? 0 : 1) - (courtMap[b.user_id] === 1 ? 0 : 1))
                          .map(p => {
                            const on = winners.includes(p.user_id);
                            return (
                              <button key={p.user_id} type="button" disabled={finalized}
                                onClick={() => setWinners(w => on ? w.filter(x => x !== p.user_id) : [...w, p.user_id])}
                                data-testid={`winner-toggle-${p.user_id}`}
                                className={`px-2 py-1 rounded-full text-[11px] font-semibold border transition-colors disabled:opacity-60 ${on ? 'bg-amber-500 border-amber-500 text-white' : 'bg-white border-slate-200 text-slate-700 hover:border-amber-400'}`}>
                                {on && <Trophy size={10} weight="fill" className="inline mr-1" />}{p.user_name}
                                {courtMap[p.user_id] === 1 && !on && <span className="ml-1 text-[9px] text-amber-600">C1</span>}
                              </button>
                            );
                          })}
                      </div>
                    </div>
                  )}

                  {mode === 'results' && (
                    <div className="rounded-xl border border-purple-200 bg-purple-50/30 p-3" data-testid="results-preview-panel">
                      <p className="text-xs font-bold uppercase tracking-wider text-purple-700 mb-2">
                        {L('Level preview', 'Level-Vorschau')}
                      </p>
                      <div className="space-y-1 max-h-72 overflow-y-auto pr-1">
                        {levelChanges.map(c => (
                          <div key={c.user_id} className="flex items-center justify-between text-[11px]" data-testid={`level-preview-${c.user_id}`}>
                            <span className="truncate flex-1">{winners.includes(c.user_id) && <Trophy size={10} weight="fill" className="inline mr-1 text-amber-500" />}{c.user_name}</span>
                            {c.missing ? (
                              <span className="text-slate-400 italic">—</span>
                            ) : (
                              <span className={`flex items-center gap-1 font-semibold ${
                                c.change < 0 ? 'text-emerald-700' : c.change > 0 ? 'text-rose-700' : 'text-slate-500'
                              }`}>
                                {c.change < 0 && <ArrowUp size={10} weight="bold" />}
                                {c.change > 0 && <ArrowDown size={10} weight="bold" />}
                                {c.change === 0 && <Equals size={10} weight="bold" />}
                                {Number(c.before).toFixed(2)}→{Number(c.after).toFixed(2)}
                                <span className="ml-1 text-[10px] font-bold">
                                  ({c.change > 0 ? '+' : ''}{Number(c.change).toFixed(2)})
                                </span>
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* No <DragOverlay>: the dragged card moves in place via
                  useDraggable.transform. See PlayerCard for why. */}
            </DndContext>

            {/* Footer actions */}
            <div className="flex flex-wrap items-center justify-end gap-2 mt-5 pt-4 border-t border-slate-200">
              {mode === 'lineup' && !isLocked && (
                <Button variant="outline" onClick={handleResetToDefault} className="border-slate-200 text-slate-600" data-testid="lineup-reset-btn">
                  {L('Reset to level-sorted', 'Auf Level-Sortierung zurücksetzen')}
                </Button>
              )}
              <div className="flex-1" />
              <Button variant="outline" onClick={onClose} className="border-slate-200 text-slate-600">
                <X size={14} className="mr-1" /> {L('Close', 'Schliessen')}
              </Button>
              {!isLocked && !finalized && (
                <>
                  <Button onClick={handleSave} disabled={saving} variant="outline" className="border-purple-200 text-purple-700 hover:bg-purple-50" data-testid="lineup-save-btn">
                    {saving ? '...' : L('Save draft', 'Entwurf speichern')}
                  </Button>
                  <Button onClick={handleConfirm} disabled={confirming || (mode === 'results' && !data?.lineup_locked) || (mode === 'lineup' && (isInvalid || confirmedCount === 0))}
                    className={`text-white font-bold ${mode === 'lineup' ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-amber-500 hover:bg-amber-600'}`}
                    data-testid="lineup-confirm-btn">
                    {confirming ? '...' : (mode === 'lineup'
                      ? L('Confirm & start tournament', 'Bestätigen & Turnier starten')
                      : L('Finalize results & update levels', 'Ergebnisse abschliessen & Levels anpassen'))}
                  </Button>
                </>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MixinLineupBoard;
